// Preview chart for ONE scan candidate (Discover step). Renders a candlestick pane with the
// candidate's simulated trades as entry/exit markers so the user can eyeball where it traded
// before committing to it.
//
// Markers are placed by TIMESTAMP (entryTimes/exitTimes on the ScanResultDto), not bar index,
// so they align to the fetched candle window regardless of how many bars we pull. We fetch the
// SAME 5000-bar window the scanner evaluated so every backtested trade has a candle to mark and
// the "N trades" count matches the markers actually drawn.
//
// Self-contained: owns its own lightweight-charts (v5) instance + candle fetch. Distinct from
// the Monitor ChartPanel (which is wired to the live SignalR feed + the netting BacktestDetail).

import { useEffect, useRef, useState } from "react";
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  createSeriesMarkers,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type UTCTimestamp,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import { Loader2 } from "lucide-react";
import { fetchLatestCandles } from "../../../api/candles";
import { toChartTime } from "../../../hooks/time";
import type { CandleDto, CandleInterval, MarketType, ScanResultDto } from "../../../api/types";

// Chart palette — inline (matches ChartPanel's hex constants; there is no shared theme module).
const BG = "#0d1117";
const TEXT = "#c9d1d9";
const GRID = "#161b22";
const BORDER = "#30363d";
const UP = "#3fb950";
const DOWN = "#f85149";

interface Props {
  coin: string;
  interval: CandleInterval;
  marketType: MarketType;
  candidate: ScanResultDto;
  /** How many recent bars to display. Default 5000 — matches the scanner's evaluation window so
   *  every backtested trade has a candle to mark (1000 would hide trades older than that). */
  bars?: number;
}

export function CandidatePreviewChart({ coin, interval, marketType, candidate, bars = 5000 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof createChart> | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const [candles, setCandles] = useState<CandleDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch candles for the display window whenever the asset tuple changes.
  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setCandles(null);
    setError(null);
    fetchLatestCandles(coin, interval, marketType, bars, ac.signal)
      .then((c) => { if (!cancelled) setCandles(c); })
      .catch((e) => { if (!cancelled && (e as Error).name !== "AbortError") setError((e as Error).message); });
    return () => { cancelled = true; ac.abort(); };
  }, [coin, interval, marketType, bars]);

  // Build the chart once.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: BG }, textColor: TEXT },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: BORDER },
      rightPriceScale: { borderColor: BORDER },
      autoSize: true,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: UP, downColor: DOWN,
      borderUpColor: UP, borderDownColor: DOWN,
      wickUpColor: UP, wickDownColor: DOWN,
    });
    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersRef.current = null;
    };
  }, []);

  // Push candles + markers whenever data or the selected candidate changes.
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || !candles) return;

    const sorted = [...candles].sort((a, b) => toTs(a.openTime) - toTs(b.openTime));
    series.setData(sorted.map((c) => ({
      time: toTs(c.openTime) as UTCTimestamp,
      open: c.open, high: c.high, low: c.low, close: c.close,
    })));

    markersRef.current?.setMarkers(buildMarkers(candidate));
    chart.timeScale().fitContent();
  }, [candles, candidate]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-[360px] rounded-lg overflow-hidden border border-border">
        <div ref={containerRef} className="absolute inset-0" />
        {!candles && !error && (
          <div className="absolute inset-0 grid place-items-center bg-card/60">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 grid place-items-center text-xs text-destructive px-4 text-center">
            Couldn't load candles: {error}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1"><span className="text-emerald-400">▲</span> entry</span>
        <span className="flex items-center gap-1"><span className="text-amber-400">▼</span> exit</span>
        <span>· {candidate.trades} simulated trades over the {bars}-bar scan window</span>
        {candidate.trades > MARKER_TRADE_CAP && (
          <span className="text-amber-300/70">· showing the most recent {MARKER_TRADE_CAP} on the chart</span>
        )}
      </div>
    </div>
  );
}

// Above this many trades we stop drawing per-trade text labels (the "#42 in" / "+0.1%" tags),
// which otherwise stack into an unreadable wall that hides the price entirely. Arrows still show.
const LABEL_LIMIT = 40;
// Hard cap on how many trades get markers at all — only the most recent are drawn so a 400-trade
// candidate stays legible. Surfaced in the footer so the count isn't silently misleading.
const MARKER_TRADE_CAP = 120;

// Build entry/exit markers from the candidate's parallel trade arrays, keyed by timestamp.
// Caps to the most-recent MARKER_TRADE_CAP trades and drops text labels past LABEL_LIMIT.
function buildMarkers(c: ScanResultDto): SeriesMarker<Time>[] {
  const markers: SeriesMarker<Time>[] = [];
  const isLong = c.direction === "long";
  const total = Math.min(c.entryTimes.length, c.exitTimes.length, c.returns.length);
  const start = Math.max(0, total - MARKER_TRADE_CAP); // most-recent N only
  const withText = total - start <= LABEL_LIMIT;
  for (let i = start; i < total; i++) {
    const entryT = toTs(c.entryTimes[i]!) as UTCTimestamp;
    const exitT = toTs(c.exitTimes[i]!) as UTCTimestamp;
    const win = c.returns[i]! >= 0;
    markers.push({
      time: entryT,
      position: isLong ? "belowBar" : "aboveBar",
      color: isLong ? UP : DOWN,
      shape: isLong ? "arrowUp" : "arrowDown",
      ...(withText ? { text: `#${i + 1} in` } : {}),
    });
    markers.push({
      time: exitT,
      position: isLong ? "aboveBar" : "belowBar",
      color: win ? "#d29922" : "#a371f7",
      shape: isLong ? "arrowDown" : "arrowUp",
      ...(withText ? { text: `${c.returns[i]! >= 0 ? "+" : ""}${c.returns[i]!.toFixed(1)}%` } : {}),
    });
  }
  // setMarkers requires ascending time order.
  return markers.sort((a, b) => (a.time as number) - (b.time as number));
}

function toTs(iso: string): number {
  // Shared UTC-defensive parser (appends Z when missing) so markers aren't TZ-shifted.
  return Math.floor(toChartTime(iso));
}
